import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { ApiError } from '@/lib/http/api-error';

import { authApi } from '../auth.api';
import { PasswordInput } from './password-input';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

interface CambiarContrasenaDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Diálogo de cambio de contraseña (barra superior → menú de usuario).
 *
 * Llama a `POST /api/auth/change-password`: el backend verifica la contraseña
 * actual, exige mínimo 6 caracteres y —para operadores— rechaza una clave igual a
 * su código. Al cambiar, revoca los refresh tokens de las sesiones previas.
 */
export function CambiarContrasenaDialog({ open, onClose }: CambiarContrasenaDialogProps) {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [aviso, setAviso] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

  const mutacion = useMutation({
    mutationFn: (datos: { currentPassword: string; newPassword: string }) =>
      authApi.cambiarContrasena(datos),
    onSuccess: () => {
      setAviso({ tipo: 'success', texto: 'Contraseña actualizada correctamente.' });
      setActual('');
      setNueva('');
      setConfirmacion('');
    },
    onError: (error) =>
      setAviso({ tipo: 'error', texto: mensajeDe(error, 'No se pudo cambiar la contraseña.') }),
  });

  // Cada vez que se abre, se limpia el estado anterior.
  useEffect(() => {
    if (open) {
      setActual('');
      setNueva('');
      setConfirmacion('');
      setAviso(null);
    }
  }, [open]);

  const errorLocal =
    nueva !== '' && nueva.length < 6
      ? 'La nueva contraseña debe tener al menos 6 caracteres.'
      : confirmacion !== '' && nueva !== confirmacion
        ? 'Las contraseñas no coinciden.'
        : undefined;

  const puedeGuardar =
    actual !== '' &&
    nueva !== '' &&
    confirmacion !== '' &&
    !errorLocal &&
    !mutacion.isPending;

  const guardar = () => {
    setAviso(null);

    if (!puedeGuardar) return;

    mutacion.mutate({ currentPassword: actual, newPassword: nueva });
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!mutacion.isPending) onClose();
      }}
      title="Cambiar contraseña"
      description="Actualiza la contraseña con la que ingresas a EDSuite."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={mutacion.isPending}>
            Cancelar
          </Button>
          <Button size="sm" loading={mutacion.isPending} disabled={!puedeGuardar} onClick={guardar}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {aviso ? (
          <Alert
            variant={aviso.tipo}
            title={aviso.tipo === 'success' ? 'Listo' : 'No se pudo cambiar la contraseña'}
          >
            {aviso.texto}
          </Alert>
        ) : null}

        <PasswordInput
          label="Contraseña actual"
          value={actual}
          onChange={(evento) => setActual(evento.target.value)}
          autoComplete="current-password"
        />

        <PasswordInput
          label="Nueva contraseña"
          value={nueva}
          onChange={(evento) => setNueva(evento.target.value)}
          autoComplete="new-password"
          error={errorLocal}
          hint="Mínimo 6 caracteres. Para operadores, no puede ser igual a su código."
        />

        <PasswordInput
          label="Confirmar nueva contraseña"
          value={confirmacion}
          onChange={(evento) => setConfirmacion(evento.target.value)}
          autoComplete="new-password"
        />
      </div>
    </Dialog>
  );
}
